import { Injectable, signal, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FileUploaderComponent } from '@tt-lib/components/file-uploader/file-uploader.component';
import { TraceRouteData, TraceRouteType, TraceMetadataType, TraceWayPointType } from '@tt-lib/models/trace-route-data';
import { BrowserSnifferService } from '@tt-lib/services/browser-sniffer.service';
import { TraceService, TraceDrawerType } from '@tt-lib/services/trace.service';
import { ViewService } from '@tt-lib/services/view.service';
import { Feature } from 'ol';
import { Extent, containsCoordinate } from 'ol/extent';
import { Geometry, MultiLineString, LineString, Circle, Point } from 'ol/geom';
import { output } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AlertService } from '@tt-lib/services/alert.service';
import { ToggleEventNameType, ToggleEventService } from '@tt-lib/services/toggle-event.service';


@Injectable({
  providedIn: 'root'
})
export class ImportService {
  constructor(
    private _translateService: TranslateService,
    public browsersniffer: BrowserSnifferService,
    private _viewService: ViewService,
    public _traceService: TraceService,
    public _alertService: AlertService,
    private toggleEventService: ToggleEventService
  ) {
    //launcher for associated gpx files
    let win: any = window;
    if ("launchQueue" in win) {
      win.launchQueue.setConsumer((launchParams) => {
        // Nothing to do when the queue is empty.
        if (!launchParams.files.length) {
          return;
        }
        // Handle the first file only.
        if (launchParams.files.length) {
          this.handleAssociatedGpxFile(launchParams.files[0]);
          return
        }
      });
    }

  }

  private isLoadingFileSubject = new BehaviorSubject<boolean>(false);
  public isLoadingFile$ = this.isLoadingFileSubject.asObservable();
  private showImportErrorSubject = new BehaviorSubject<any>(null);
  public showImportError$ = this.showImportErrorSubject.asObservable();

  async handleAssociatedGpxFile(file) {
    const blob = await file.getFile();
    this.importGpxFile(blob);
  }

  importGpxFile(gpxFile: File) {
    //there will be a zoom after data load
    //geolocalisation component is listening
    //the timeout below also makes sure that the other component is warned in time
    this.toggleEventService.toggleEvent({
      forwardedFor: 'gpx-import',
      eventName: ToggleEventNameType.FILE_IMPORT
    });

    // this.isLoadingFileSubject.next(true);
    // mobile when editing is active
    if (this._traceService.isInEditingMode()) {
      this._traceService.closeCreateMobile();
    }

    //alert('import gpx ' + gpxFile.size + ' ' + gpxFile.name)
    window.setTimeout(() => {

      let fileReader = new FileReader();
      fileReader.addEventListener('error', (event) => {
        this.showImportError(event);
      });

      fileReader.addEventListener('load', (event) => {
        try {
          const source = new DOMParser().parseFromString(
            typeof event.target.result === 'string'
              ? event.target.result
              : new TextDecoder().decode(event.target.result),
            'text/xml'
          );

          let gpxData: any = this._xmlToJson(source);
          gpxData = gpxData && gpxData.gpx ? gpxData.gpx : {};

          let validPointPositions = true;
          let validTypes = true;
          const mapExtent: Extent = [];
          //mapExtent.push(...TraceRouteData.updatePointProjection([this._viewService.getProjection().getExtent()[0], this._viewService.getProjection().getExtent()[1]], this._viewService.getProjection().getCode(), 'EPSG:4326'));
          //mapExtent.push(...TraceRouteData.updatePointProjection([this._viewService.getProjection().getExtent()[2], this._viewService.getProjection().getExtent()[3]], this._viewService.getProjection().getCode(), 'EPSG:4326'));
          mapExtent.push(...TraceRouteData.updatePointProjection([this._traceService.extentRestrictionLB08[0], this._traceService.extentRestrictionLB08[1]], 'EPSG:3812', 'EPSG:4326'));
          mapExtent.push(...TraceRouteData.updatePointProjection([this._traceService.extentRestrictionLB08[2], this._traceService.extentRestrictionLB08[3]], 'EPSG:3812', 'EPSG:4326'));

          //add gpx tracks and gpx routes to model
          const routes: TraceRouteType[] = this._getGpxRoutes(gpxData);

          routes.every((route) => {
            //normally the object should be valid, but we do a check to be sure that the model wasn't changed without updating this import function
            validTypes = TraceRouteData.isTraceRouteType(TraceRouteData.getFilteredTraceRouteDataObject(route, true));

            route.routePoints.every((routePoint) => {
              if (!containsCoordinate(mapExtent, [routePoint.x, routePoint.y])) {
                validPointPositions = false;
                return false;
              }
              return true;
            });

            return validTypes && validPointPositions;
          });

          if (!validTypes) {
            throw new Error("one or more route objects are not valid TraceRouteType's, please check the import function");
          }

          if (!validPointPositions) {
            throw new Error("point outside map extent");
          }

          //add gpx metadata to model (name/desc)
          //it's possible that the name/desc are only available in a gpx track or gpx route (we only look at the first track or route)
          const metadata: TraceMetadataType = this._getGpxMetadata(
            gpxData,
            routes
          );

          if (routes) {
            //if necessary name/desc from routes[0] are copied to the metadata tag (see method _getGpxMetadata)
            //for now we don't keep seperate name/desc in the routes objects (because they are not shown in our viewer and not editable)
            routes.forEach(route => {
              if (route.name) {
                delete route.name;
              }
              if (route.desc) {
                delete route.desc;
              }
            });
          }

          //normally the object should be valid, but we do a check to be sure that the model wasn't changed without updating this import function
          if (!TraceRouteData.isTraceMetadataType(TraceRouteData.getFilteredTraceRouteDataObject(metadata, true))) {
            throw new Error("metadata object is not a valid TraceMetadataType, please check the import function");
          }

          //add gpx wayPoints to model
          let wayPoints: TraceWayPointType[] =
            gpxData && gpxData.wpt ? this._getGpxWayPoints(gpxData.wpt, false) : [];

          wayPoints.every((wayPoint) => {
            //normally the object should be valid, but we do a check to be sure that the model wasn't changed without updating this import function
            validTypes = TraceRouteData.isTraceWayPointType(TraceRouteData.getFilteredTraceRouteDataObject(wayPoint, true));

            if (!containsCoordinate(mapExtent, [wayPoint.x, wayPoint.y])) {
              validPointPositions = false;
            }

            return validTypes && validPointPositions;
          });

          if (!validTypes) {
            throw new Error("one or more wayPoint objects are not valid TraceWayPointType's, please check the import function");
          }

          if (!validPointPositions) {
            throw new Error("point outside map extent");
          }

          if (routes.length > 0 && routes[0].routePoints.length > 1 && wayPoints.length > 0) {
            //make sure waypoints array is logically ordered along the route
            //return in current map projection
            wayPoints = TraceRouteData.orderWayPointsAlongRoute(
              wayPoints,
              routes,
              'EPSG:4326',
              this._viewService.getProjection().getCode(),
              true
            );
          } else {
            //convert to current map projection
            wayPoints.forEach((wayPoint) => {
              const coordinate = TraceRouteData.updatePointProjection(
                [wayPoint.x, wayPoint.y],
                'EPSG:4326',
                this._viewService.getProjection().getCode()
              );
              wayPoint.x = coordinate[0];
              wayPoint.y = coordinate[1];
            });
          }

          //the first and last point are normally initialized with value false, the rest is initialized with value true
          //unless it was an import of a gpx file created by tmv
          let fixedRoutePointCount = 0;

          //convert to current map projection
          routes.forEach((route) => {
            route.routePoints.forEach((routePoint) => {
              fixedRoutePointCount += typeof routePoint.autoDraw === 'boolean' && !routePoint.autoDraw ? 1 : 0;

              const coordinate = TraceRouteData.updatePointProjection(
                [routePoint.x, routePoint.y],
                'EPSG:4326',
                this._viewService.getProjection().getCode()
              );
              routePoint.x = coordinate[0];
              routePoint.y = coordinate[1];
            });
          });

          //update autoDraw value
          //the first and last point are normally initialized with value false, the rest is initialized with value true
          //unless it was an import of a gpx file created by tmv
          if (fixedRoutePointCount <= 2) {
            /*
            routes.forEach((route) => {
              let distanceSinceLastFixedPoint = 0;
              let diffRotationLastFixedPoint = 0;
              let indexLastFixedPoint = -1;
              for (let i = 1; i < route.routePoints.length - 1; i++) {
                const dx0 = route.routePoints[i].x - route.routePoints[i - 1].x;
                const dy0 = route.routePoints[i].y - route.routePoints[i - 1].y;
                let rotation_0 = Math.atan2(dy0, dx0) * 180 / Math.PI;
                rotation_0 += rotation_0 >= 0 ? 0 : 360;
                const dx1 = route.routePoints[i + 1].x - route.routePoints[i].x;
                const dy1 = route.routePoints[i + 1].y - route.routePoints[i].y;
                let rotation_1 = Math.atan2(dy1, dx1) * 180 / Math.PI;
                rotation_1 += rotation_1 >= 0 ? 0 : 360;
                const diff_rotation = Math.abs(rotation_0 - rotation_1);
                distanceSinceLastFixedPoint += new LineString([[route.routePoints[i - 1].x, route.routePoints[i - 1].y], [route.routePoints[i].x, route.routePoints[i].y]]).getLength();
  
                if (diff_rotation >= 45 && (distanceSinceLastFixedPoint >= 100 || diff_rotation > diffRotationLastFixedPoint)) {
                  if (distanceSinceLastFixedPoint < 10 && indexLastFixedPoint > -1) {
                    route.routePoints[indexLastFixedPoint].autoDraw = true;
                  }
                  indexLastFixedPoint = i;
                  distanceSinceLastFixedPoint = 0;
                  diffRotationLastFixedPoint = diff_rotation;
                  route.routePoints[i].autoDraw = false;
                }
  
                // if (diff_rotation >= 30 && (distanceSinceLastFixedPoint >= 50 || diff_rotation > diffRotationLastFixedPoint)) {
                //   if (distanceSinceLastFixedPoint < 10 && indexLastFixedPoint > -1) {
                //     route.routePoints[indexLastFixedPoint].autoDraw = true;
                //   }
                //   indexLastFixedPoint = i;
                //   distanceSinceLastFixedPoint = 0;
                //   diffRotationLastFixedPoint = diff_rotation;
                //   route.routePoints[i].autoDraw = false;
                // }
              }
            });
            */
            //alternative, using the simplify method (for linestrings, this uses the Douglas Peucker algorithm)
            routes.forEach(route => {
              let routeAsFeature: Feature<Geometry> = TraceRouteData.convertTraceRouteToLineStringOrPointFeature(route);

              if (routeAsFeature && (routeAsFeature.getGeometry() instanceof MultiLineString || routeAsFeature.getGeometry() instanceof LineString)) {
                let lineStrings: LineString[] = [];

                if (routeAsFeature.getGeometry() instanceof MultiLineString) {
                  lineStrings = (routeAsFeature.getGeometry() as MultiLineString).getLineStrings();
                } else {
                  lineStrings.push(routeAsFeature.getGeometry() as LineString)
                }

                let routeLength = 0;
                lineStrings.forEach((lineString: LineString) => {
                  routeLength += lineString.getLength();
                });

                lineStrings.forEach((lineString: LineString) => {
                  const coordinates = (lineString.simplify(routeLength <= 25000 ? 10 : routeLength <= 50000 ? 20 : routeLength <= 100000 ? 50 : 100) as LineString).getCoordinates();

                  for (let i = 1; i < route.routePoints.length - 1; i++) {
                    for (let y = 0; y < coordinates.length; y++) {
                      if (new Circle(coordinates[y], 2).intersectsCoordinate([route.routePoints[i].x, route.routePoints[i].y])) {
                        route.routePoints[i].autoDraw = false;
                      }
                    }
                  }
                });
              }

            });
          }

          //const size = TraceRouteData.getTraceRouteDataImportSize(TraceRouteData.getFilteredTraceRouteDataObject(metadata, true), TraceRouteData.getFilteredTraceRouteDataArray(wayPoints, true), TraceRouteData.getFilteredTraceRouteDataArray(routes, true));
          const size = TraceRouteData.getTraceRouteDataImportSize(metadata, wayPoints, routes, null);
          let canSave = size >= 0 && this._traceService.getLocalStorageFreeSpace() >= size;
          console.log('size of imported route data is ' + (size >= 0 ? (size / 1048576).toFixed(2) + ' MB' : 'unknown'));
          if (!canSave && size >= 0) {
            console.log('we can\'t save the route data in localStorage, there\'s not enough free space');
          }

          if (canSave) {
            //we could just update the route name with a suffix if already in history list, but instead we'll first try to remove an old identical import
            //we'll compare previous imports with the most recent one and delete the old one if necessary 
            this._traceService.removeSameImportsFromHistory(metadata, wayPoints, routes, null);
          }

          this._traceService.setInEditingMode(false);

          const xsmallScreen = this.browsersniffer.isPhone();
          this._traceService.openTraceTab(xsmallScreen ? TraceDrawerType.CLOSE_AND_VIEW_ROUTE : TraceDrawerType.VIEW_ROUTE);

          window.setTimeout(
            () => {
              try {
                //create new history item
                this._traceService.addNewHistoryItemForImport();
                //no need to rename, we used method removeSameImportsFromHistory
                //metadata.name = this._traceService.createUniqueNameForNewHistoryItem(metadata.name);

                this._traceService.setTraceRouteData(
                  metadata,
                  wayPoints,
                  routes,
                  null,
                  true
                );

                if (canSave) {
                  //this method will also update the newly created history item and call _saveTraceRouteHistoryToLocalStorage
                  this._traceService.saveTraceRouteDataToLocalStorage();
                } else {
                  this._traceService.setPermalinkParam(false);
                }
              } catch (error) {
                this.showImportError(error);
              }

              // this._fileUploader.isLoadingFile = false;
              this.isLoadingFileSubject.next(false);
            },
            xsmallScreen ? 500 : 100
          );

        } catch (error) {
          // this._fileUploader.isLoadingFile = false;
          this.isLoadingFileSubject.next(false);

          this.showImportError(error);
        }
      });

      fileReader.readAsText(gpxFile);
    }, 1000);
  }

  private _getGpxMetadata(
    gpxData: any,
    routes: TraceRouteType[]
  ): TraceMetadataType {
    let metadata: TraceMetadataType = null;
    let metadataNameOrDescFound = false;

    if (
      gpxData &&
      gpxData.metadata
    ) {
      const time = this._toDate(gpxData.metadata.time);
      metadata = {
        name: TraceRouteData.getFilteredHtmlString(gpxData.metadata.name, true, false),
        desc: TraceRouteData.getFilteredHtmlString(gpxData.metadata.desc, false, false),
        time: time === null ? -1 : time.getTime(),
      };
      metadataNameOrDescFound = typeof metadata.name === 'string' || typeof metadata.desc === 'string';
    }

    //if no name/desc was found, search in first route
    if (!metadataNameOrDescFound && routes && routes.length > 0 && (routes[0].name || routes[0].desc)) {
      metadata = metadata ? metadata : { time: -1 };
      //if available, routes[0].name and/or routes[0].desc are already checked for string type & html content
      metadata.name = routes[0].name ? routes[0].name : null;
      metadata.desc = routes[0].desc ? routes[0].desc : null;
    }

    return metadata === null ? { time: -1 } : metadata;
  }

  private _getGpxRoutes(gpxData: any): TraceRouteType[] {
    const routes: TraceRouteType[] = [];

    const rte =
      gpxData && gpxData.rte
        ? TraceRouteData.getNativeClass(gpxData.rte) === 'object'
          ? [gpxData.rte]
          : gpxData.rte
        : [];

    rte.forEach((oneRte: any) => {
      const rtepts = this._getGpxWayPoints(oneRte.rtept, true);

      if (rtepts.length > 0) {
        const fixedPointIndexes: string[] = typeof oneRte.extensions === "object" && typeof oneRte.extensions['tmv:rte_extension'] === "object" && typeof oneRte.extensions['tmv:rte_extension']['tmv:rte_fixedpoint_indexes'] === 'object' && Array.isArray(oneRte.extensions['tmv:rte_extension']['tmv:rte_fixedpoint_indexes']['tmv:num']) ? oneRte.extensions['tmv:rte_extension']['tmv:rte_fixedpoint_indexes']['tmv:num'] : [];

        fixedPointIndexes.forEach((fixedPointIndex: string) => {
          if (/^[0-9]+$/.test(fixedPointIndex)) {
            const fixedPointIndexAsNbr = parseInt(fixedPointIndex);
            //start and end will already be set to false
            if (fixedPointIndexAsNbr > 0 && fixedPointIndexAsNbr < (rtepts.length - 1)) {
              rtepts[fixedPointIndexAsNbr].autoDraw = false;
            }
          }
        });

        routes.push({
          wasTrack: false,
          name: TraceRouteData.getFilteredHtmlString(oneRte.name, true, false),
          desc: TraceRouteData.getFilteredHtmlString(oneRte.desc, false, false),
          routePoints: rtepts,
        });
      }
    });

    const trk =
      gpxData && gpxData.trk
        ? TraceRouteData.getNativeClass(gpxData.trk) === 'object'
          ? [gpxData.trk]
          : gpxData.trk
        : [];

    trk.forEach((oneTrk: any) => {
      //we add trkpt from every trkseg to one array
      const trkpt = [];

      const trkseg = oneTrk.trkseg
        ? TraceRouteData.getNativeClass(oneTrk.trkseg) === 'object'
          ? [oneTrk.trkseg]
          : oneTrk.trkseg
        : [];

      trkseg.forEach((oneTrekseg: any) => {
        const trksegTrkpt = oneTrekseg.trkpt
          ? TraceRouteData.getNativeClass(oneTrekseg.trkpt) === 'object'
            ? [oneTrekseg.trkpt]
            : oneTrekseg.trkpt
          : [];

        const trkpt2 = this._getGpxWayPoints(trksegTrkpt, true);

        if (trkpt2.length > 0) {

          const fixedPointIndexes: string[] = typeof oneTrekseg.extensions === "object" && typeof oneTrekseg.extensions['tmv:trkseg_extension'] === "object" && typeof oneTrekseg.extensions['tmv:trkseg_extension']['tmv:trkseg_fixedpoint_indexes'] === 'object' && Array.isArray(oneTrekseg.extensions['tmv:trkseg_extension']['tmv:trkseg_fixedpoint_indexes']['tmv:num']) ? oneTrekseg.extensions['tmv:trkseg_extension']['tmv:trkseg_fixedpoint_indexes']['tmv:num'] : [];

          fixedPointIndexes.forEach((fixedPointIndex: string) => {
            if (/^[0-9]+$/.test(fixedPointIndex)) {
              const fixedPointIndexAsNbr = parseInt(fixedPointIndex);
              //start and end will already be set to false
              if (fixedPointIndexAsNbr > 0 && fixedPointIndexAsNbr < (trkpt2.length - 1)) {
                trkpt2[fixedPointIndexAsNbr].autoDraw = false;
              }
            }
          });

          trkpt.push(...trkpt2);
        }
      });

      if (trkpt.length > 0) {
        routes.push({
          wasTrack: true,
          name: TraceRouteData.getFilteredHtmlString(oneTrk.name, true, false),
          desc: TraceRouteData.getFilteredHtmlString(oneTrk.desc, false, false),
          routePoints: trkpt,
        });
      }
    });

    return routes;
  }

  private _getGpxWayPoints(gpxWaypointData: any, isRoutePoints: boolean): TraceWayPointType[] {
    const wayPoints: TraceWayPointType[] = [];

    gpxWaypointData = gpxWaypointData
      ? TraceRouteData.getNativeClass(gpxWaypointData) === 'object'
        ? [gpxWaypointData]
        : gpxWaypointData
      : [];


    gpxWaypointData.forEach((wayPoint: any) => {
      //we could return the whole object but we don't want extra properties inside the object
      if (
        wayPoint &&
        wayPoint['@attributes'] &&
        wayPoint['@attributes'].lon &&
        wayPoint['@attributes'].lat
      ) {
        try {
          const time = this._toDate(wayPoint.time);
          const parsedX = parseFloat(wayPoint['@attributes'].lon);
          const parsedY = parseFloat(wayPoint['@attributes'].lat);

          const newPointData = {
            x: parsedX,
            y: parsedY,
            z: wayPoint.ele ? parseFloat(wayPoint.ele) : 0,
            formattedXY: TraceRouteData.formatPointForHtmlView(new Point([parsedX, parsedY]), 'EPSG:4326')
          };

          if (isRoutePoints) {
            //autoDraw will still be updated below for first and last (and also in _getGpxRoutes)
            newPointData['autoDraw'] = true;
            newPointData['time'] = time === null ? -1 : time.getTime();
          } else {
            newPointData['name'] = TraceRouteData.getFilteredHtmlString(wayPoint.name, true, false);
            newPointData['desc'] = TraceRouteData.getFilteredHtmlString(wayPoint.desc, false, false);
            newPointData['symb'] = wayPoint.symb ? wayPoint.symb : null;
          }

          wayPoints.push(newPointData);
        } catch (error) {
          console.log(
            'one of there values could not be transformed from string to number lat/lon/ele : ' +
            error
          );
        }
      }
    });

    if (isRoutePoints && wayPoints.length > 0) {
      wayPoints[0].autoDraw = false;
      wayPoints[wayPoints.length - 1].autoDraw = false;
    }

    return wayPoints;
  }

  private _toDate(dateString: string) {
    const newDate = new Date(dateString);
    return isNaN(new Date(newDate).getDate()) ? null : newDate;
  }

  showImportError(error: any) {
    // this._fileUploader.showImportError();
    this.showImportErrorSubject.next(error);
    if (error) {
      console.log("an error occured during import:");
      console.dir(error);
      if (typeof error.message === "string" && error.message === "point outside map extent") {
        this._alertService.info(this._translateService.instant('trace.import-outside-map'), { 'autoClose': true });
      }
    }
  }
  /**
   * https://davidwalsh.name/convert-xml-json
   * @param xml
   * @returns json object
   */
  private _xmlToJson(xml: any): {} {
    let obj = {};

    switch (xml.nodeType) {
      case 3: {
        //text
        obj = xml.nodeValue;
        break;
      }
      case 4: {
        //cdata section
        const cdataValue = xml.childNodes.item(0).wholeText?.trim();
        obj = typeof cdataValue === "string" ? cdataValue : null;
        break;
      }
      case 8: {
        //we ignore comments
        obj = null;
        break;
      }
      default: {
        if (xml.nodeType === 1) {
          //element
          //get attributes
          if (xml.attributes.length > 0) {
            obj['@attributes'] = {};
            for (let j = 0; j < xml.attributes.length; j++) {
              const attribute = xml.attributes.item(j);
              obj['@attributes'][attribute.nodeName] = attribute.nodeValue;
            }
          }
        }

        //process children
        if (xml.hasChildNodes()) {
          //if all text nodes inside, get concatenated text
          var textNodes = [].slice.call(xml.childNodes).filter(function (node) {
            return node.nodeType === 3
              //cdata
              || node.nodeType === 4
              //comment
              || node.nodeType === 8;
          });
          if (xml.childNodes.length === textNodes.length) {
            const concatenatedText = [].slice.call(xml.childNodes).reduce(function (text: string, node) {
              if (node.nodeType === 3) {
                return text + node.nodeValue;
              } else if (node.nodeType === 4) {
                const cdataValue = xml.childNodes.item(0).wholeText?.trim();
                return text + (typeof cdataValue === "string" ? cdataValue : "");
              } else {
                //comment is ignored
                return text
              }
            }, '');

            if (TraceRouteData.isEmptyObject(obj)) {
              obj = concatenatedText;
            } else {
              obj['#text'] = concatenatedText;
            }
          } else {
            let foundValidContent = false;
            for (var i = 0; i < xml.childNodes.length; i++) {
              var item = xml.childNodes.item(i);
              var nodeName = item.nodeName;
              var nodeContent = this._xmlToJson(item);
              if (nodeContent) {
                foundValidContent = true;
                /*
                  key already present in object?
                  if not, add key with value nodeContent
                  if yes and if not yet an array, convert old value into an array and then add nodeContent to array
                  */
                if (typeof obj[nodeName] === 'undefined') {
                  obj[nodeName] = nodeContent;
                } else {
                  if (typeof obj[nodeName].push == 'undefined') {
                    var old = obj[nodeName];
                    obj[nodeName] = [];
                    obj[nodeName].push(old);
                  }
                  obj[nodeName].push(nodeContent);
                }
              }
            }

            if (!foundValidContent && TraceRouteData.isEmptyObject(obj)) {
              obj = null;
            }
          }
        }

      }
    }

    return obj;
  }


}
